import React from 'react';

const Privacy = () => {
  return (
    <div>
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-blue-600 to-purple-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">Privacy Policy</h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
              Your privacy matters to us. Learn how we collect, use, and protect your information.
            </p>
            <p className="text-sm text-blue-200 mt-4">Last updated: September 1, 2025</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        {/* Scope Notice */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-12">
          <p className="text-blue-800 font-medium">
            This policy applies to all users in the United States. Certain rights for residents of California and other states with consumer privacy laws are described below.
          </p>
        </div>

        {/* What We Collect */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">What We Collect</h2>
          <p className="text-gray-700 mb-6">
            We collect information you provide and information generated when you use the Service:
          </p>
          
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Account & Profile</h3>
              <p className="text-gray-700">Name, email, phone, address, organization and role (e.g., board member, homeowner).</p>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Community Data</h3>
              <p className="text-gray-700">Property address, HOA membership, requests, messages, attachments.</p>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Usage & Device</h3>
              <p className="text-gray-700">Pages viewed, feature usage, time stamps, IP address, device and browser details, cookies/SDK events for analytics and performance.</p>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Communications</h3>
              <p className="text-gray-700">Your preferences for email/SMS and notification frequency.</p>
            </div>
          </div>
        </section>

        {/* How We Use Information */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">How We Use Information</h2>
          <div className="bg-white rounded-lg shadow-md p-6">
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <div className="flex-shrink-0 w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3"></div>
                <span>Provide and secure the Service</span>
              </li>
              <li className="flex items-start">
                <div className="flex-shrink-0 w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3"></div>
                <span>Operate chats, announcements, polls, events, ticketing, and workflows</span>
              </li>
              <li className="flex items-start">
                <div className="flex-shrink-0 w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3"></div>
                <span>Send service and transactional messages</span>
              </li>
              <li className="flex items-start">
                <div className="flex-shrink-0 w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3"></div>
                <span><strong>Newsletters & product updates</strong> (you can opt out any time)</span>
              </li>
              <li className="flex items-start">
                <div className="flex-shrink-0 w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3"></div>
                <span>Improve the Service via analytics and diagnostics</span>
              </li>
            </ul>
          </div>
        </section>

        {/* What We Don't Do */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">What We Don't Do</h2>
          <div className="bg-green-50 border border-green-200 rounded-lg p-6">
            <ul className="space-y-3 text-green-800">
              <li className="flex items-start">
                <svg className="w-5 h-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span><strong>We do not sell your personal information.</strong></span>
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>We do not share personal information for cross‑context behavioral advertising.</span>
              </li>
            </ul>
          </div>
        </section>

        {/* Sharing */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Sharing</h2>
          <p className="text-gray-700 mb-6">We share data with:</p>
          
          <div className="space-y-4">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Service providers (processors)</h3>
              <p className="text-gray-700">Hosting, email/SMS, analytics, support tools—bound by contract and used only to perform services for us.</p>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Your organization</h3>
              <p className="text-gray-700">Admins/board members may see data associated with your community.</p>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Legal</h3>
              <p className="text-gray-700">To comply with law, enforce terms, or respond to <strong>valid subpoenas or other lawful requests</strong>. We notify the account owner when legally permitted.</p>
            </div>
          </div>
        </section>

        {/* SMS/Email Preferences */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">SMS/Email Preferences</h2>
          <div className="bg-white rounded-lg shadow-md p-6">
            <p className="text-gray-700 mb-4">
              You can control which messages you receive and how often. Opt out using in‑message links or your profile settings. Message and data rates may apply.
            </p>
            <p className="text-gray-700">
              By enabling SMS, you consent to receive texts from HOA Manager Pro; consent is not a condition of purchase.
            </p>
          </div>
        </section>

        {/* Data Retention */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Data Retention</h2>
          <div className="bg-white rounded-lg shadow-md p-6">
            <p className="text-gray-700">
              We retain data for as long as your account is active or as needed to provide the Service and meet legal obligations. You may request deletion as described below.
            </p>
          </div>
        </section>

        {/* Your Privacy Rights */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Your Privacy Rights</h2>
          <div className="bg-white rounded-lg shadow-md p-6">
            <p className="text-gray-700 mb-4">
              Depending on your state (including <strong>California</strong> under CCPA/CPRA), you may have the right to:
            </p>
            <ul className="space-y-2 text-gray-700 mb-6">
              <li className="flex items-start">
                <div className="flex-shrink-0 w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3"></div>
                <span>Access/know the categories and specific pieces of personal information we hold</span>
              </li>
              <li className="flex items-start">
                <div className="flex-shrink-0 w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3"></div>
                <span>Correct inaccuracies</span>
              </li>
              <li className="flex items-start">
                <div className="flex-shrink-0 w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3"></div>
                <span>Delete personal information</span>
              </li>
              <li className="flex items-start">
                <div className="flex-shrink-0 w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3"></div>
                <span>Receive a portable copy (data export)</span>
              </li>
              <li className="flex items-start">
                <div className="flex-shrink-0 w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3"></div>
                <span>Limit the use of certain sensitive information</span>
              </li>
              <li className="flex items-start">
                <div className="flex-shrink-0 w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3"></div>
                <span>Opt out of sale or sharing (we do not sell or share for cross‑context behavioral advertising)</span>
              </li>
            </ul>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">How to exercise:</h4>
              <p className="text-blue-800">
                Email <a href="mailto:privacy@hoamanagerpro.us" className="text-blue-600 hover:text-blue-800 underline">privacy@hoamanagerpro.us</a> or use the in‑app Privacy Request form. We will verify your request and respond within the time required by law. Authorized agents may act on your behalf with appropriate documentation.
              </p>
            </div>
          </div>
        </section>

        {/* Additional Sections */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          
          {/* Children's Privacy */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Children's Privacy</h3>
            <p className="text-gray-700">
              The Service is not directed to children under 13, and we do not knowingly collect personal information from children.
            </p>
          </div>

          {/* Security */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Security</h3>
            <p className="text-gray-700">
              We use administrative, technical, and physical safeguards as described on our Security page. No system is 100% secure; please use strong passwords and enable MFA where available.
            </p>
          </div>

          {/* International Use */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">International Use</h3>
            <p className="text-gray-700">
              Our Service is intended for U.S. organizations. If you access it from outside the U.S., data may be transferred to and processed in the U.S.
            </p>
          </div>

          {/* Changes */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Changes</h3>
            <p className="text-gray-700">
              We may update this Policy. We'll post the new date and, when material, provide notice.
            </p>
          </div>
        </div>

        {/* Contact */}
        <section className="bg-gradient-to-r from-blue-600 to-purple-700 rounded-lg text-white p-8 text-center">
          <h2 className="text-2xl font-bold mb-4">Questions About Privacy?</h2>
          <p className="text-blue-100 mb-6">
            Contact our privacy team for any questions about this policy or your data rights.
          </p>
          <a
            href="mailto:privacy@hoamanagerpro.us"
            className="inline-flex items-center px-6 py-3 border border-white text-base font-medium rounded-md text-blue-600 bg-white hover:bg-gray-50 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            privacy@hoamanagerpro.us
          </a>
        </section>
      </div>
    </div>
  );
};

export default Privacy;